// Keuzelijst voor 'Vraag koppelen' in het Vraag-blok van een live rapportage.
//
// Nodig omdat de lijst eerst alle vragen van alle vragenlijsten door elkaar
// toonde, gesorteerd op Position (1..n per vragenlijst): de vragen van een
// nieuwe vragenlijst stonden verspreid tussen de oude, zonder te zeggen waar
// ze bij hoorden, en vijf matrixvragen met dezelfde titel waren niet uit
// elkaar te houden. Updateverzoek van Drenthe beweegt, 2026-10-02.

import notDeleted from '../../../../helpers/notDeleted'
import flattenFieldsInOrder from '../../../../helpers/questionnaire/flattenFieldsInOrder'
import isQuestionField from '../../../../helpers/questionnaire/isQuestionField'

/**
 * @typedef {object} QuestionChoice
 * @property {string} value - ID van het QuestionnaireField
 * @property {string} label - 'Vragenlijst · n. Vraag'
 */

/**
 * @param {*} timestamp - Firestore Timestamp of iets anders
 * @returns {number}
 */
const toMillis = (timestamp) => (typeof timestamp?.toMillis === 'function' ? timestamp.toMillis() : 0)

/**
 * @param {*} timestamp - Firestore Timestamp of iets anders
 * @returns {string} 'dd-mm-jjjj', of '' als er geen datum is
 */
const toDateLabel = (timestamp) => {
    const ms = toMillis(timestamp)
    if (!ms) return ''
    const d = new Date(ms)
    const p2 = (n) => String(n).padStart(2, '0')
    return `${p2(d.getDate())}-${p2(d.getMonth() + 1)}-${d.getFullYear()}`
}

/**
 * Maakt gelijke labels van vragen zonder vragenlijst uit elkaar te houden.
 * Testronde 2026-10-02 (B2): tientallen regels die letterlijk gelijk waren.
 * Een uniek label blijft kaal; bij een dubbel komt de aanmaakdatum erachter,
 * en is ook die gelijk, een volgnummer.
 *
 * @param {Array<{field: object, base: string}>} rows - In weergavevolgorde
 * @returns {QuestionChoice[]}
 */
const distinguish = (rows) => {
    const tally = (keys) => {
        const counts = {}
        keys.forEach(k => { counts[k] = (counts[k] || 0) + 1 })
        return counts
    }

    const baseCount = tally(rows.map(r => r.base))
    const withDate = rows.map(r => {
        const datum = baseCount[r.base] > 1 ? toDateLabel(r.field.Timestamp) : ''
        return { ...r, suffix: datum ? `aangemaakt ${datum}` : '' }
    })

    const suffixKey = (r) => `${r.base}|${r.suffix}`
    const suffixCount = tally(withDate.map(suffixKey))
    const seen = {}

    return withDate.map(r => {
        const key = suffixKey(r)
        const parts = r.suffix ? [r.suffix] : []
        if (suffixCount[key] > 1) {
            seen[key] = (seen[key] || 0) + 1
            parts.push(String(seen[key]))
        }
        return { value: r.field.ID, label: parts.length ? `${r.base} (${parts.join(', ')})` : r.base }
    })
}

/**
 * @param {Array<object>} fields - QuestionnaireFields van de organisatie
 * @param {Array<object>} questionnaires - Questionnaires van de organisatie, óók die in de prullenbak
 * @returns {QuestionChoice[]} Nieuwste vragenlijst eerst, daarbinnen in vragenlijstvolgorde;
 *   daarna vragenlijsten in de prullenbak, en als laatste de vragen zonder vragenlijst
 */
const buildQuestionChoices = (fields, questionnaires) => {
    // Ongefilterd binnen: prullenbak en 'bestaat niet' zijn twee toestanden.
    // AI-CONTEXT: See docs/pitfalls.md#database-hard-to-soft-delete-leaves-old-orphans
    // Levend wint: staat één ID zowel levend als verwijderd, dan telt hij als levend.
    const seenQuestionnaires = new Set()
    const sortedQuestionnaires = [...questionnaires.filter(notDeleted), ...questionnaires.filter(q => !notDeleted(q))]
        .filter(q => (seenQuestionnaires.has(q.ID) ? false : seenQuestionnaires.add(q.ID)))
        // ID als laatste sleutel: bij een gelijk of ontbrekend tijdstempel blijft
        // de volgorde vast in plaats van de Firestore-volgorde te volgen.
        // AI-CONTEXT: See docs/pitfalls.md#database-coarse-sort-key-ties-break-arbitrarily
        .sort((a, b) => (notDeleted(b) - notDeleted(a))
            || (toMillis(b.Timestamp) - toMillis(a.Timestamp))
            || String(a.ID).localeCompare(String(b.ID)))
    const known = new Set(sortedQuestionnaires.map(q => q.ID))
    // Eén keuze per ID. Twee documenten met hetzelfde ID (gezien bij Vitaminds)
    // gaven dubbele React-keys, en dan toonde de zoekfunctie oude regels die niet
    // bij de zoekterm hoorden.
    const seen = new Set()
    const ordered = fields
        .filter(f => (seen.has(f.ID) ? false : seen.add(f.ID)))
        // ID als laatste sleutel: losse vragen uit verschillende vragenlijsten
        // delen vaak hun Position, en dan mag de Firestore-volgorde niet bepalen
        // wie welk volgnummer krijgt.
        // AI-CONTEXT: See docs/pitfalls.md#database-coarse-sort-key-ties-break-arbitrarily
        .sort((a, b) => ((Number(a.Position) || 0) - (Number(b.Position) || 0))
            || String(a.ID).localeCompare(String(b.ID)))
    const text = (field) => (field.Question || '').trim() || '(vraag zonder tekst)'

    // Formuliervolgorde via de gedeelde helper: een vraag in een sectie staat op
    // SectionPosition, niet op Position. Die helper laat een vraag weg waarvan de
    // sectie niet meer bestaat; zo'n vraag kan nog antwoorden hebben, dus hij
    // komt achteraan erbij. Pas daarna de sectiekoppen eruit.
    const inFormOrder = (own) => {
        const flat = flattenFieldsInOrder(own)
        const placed = new Set(flat.map(f => f.ID))
        return [...flat, ...own.filter(f => !placed.has(f.ID))]
    }
    const grouped = sortedQuestionnaires.flatMap(questionnaire => inFormOrder(
        ordered.filter(f => f.QuestionnaireID === questionnaire.ID))
        .filter(isQuestionField)
        .map((field, index) => ({
            value: field.ID,
            label: `${questionnaire.Title || 'Vragenlijst zonder naam'}${notDeleted(questionnaire) ? '' : ' (in prullenbak)'} · ${index + 1}. ${text(field)}`,
        })))

    // Vragen waarvan de vragenlijst niet (meer) bestaat: een vragenlijst die
    // tot 2026-09-16 hard verwijderd werd liet zijn vragen achter, en sommige velden hebben
    // nooit een vragenlijst gehad. Ze kunnen nog antwoorden hebben, dus ze
    // blijven kiesbaar, maar onderaan en met de reden erbij.
    const loose = distinguish(ordered
        .filter(isQuestionField)
        .filter(f => !known.has(f.QuestionnaireID))
        .map(field => ({ field, base: `Zonder vragenlijst · ${text(field)}` })))

    return [...grouped, ...loose]
}

export default buildQuestionChoices
